/**
 * Moved-owner trigger recovery, at the organization level (#287 pass 2).
 *
 * A trigger's owner can be moved out of the organization while an order it placed
 * is still resting on that organization's keys. The trader is locked out by the
 * server, and nobody else has a route to the order — this panel is that route.
 *
 * The design rule throughout: the SERVER decides. Every state, every reason, and
 * the presence of an action come off the durable snapshot, and after acting the
 * list is refetched rather than patched. Synthesizing "complete" in the browser
 * would be a claim about somebody's money that no response supports.
 *
 * Scope mirrors the endpoint. A manager sends no organization at all (theirs is
 * read from the database and cannot be widened by a query); an admin must have one
 * selected, and the panel stays inert until they do — the endpoint refuses an
 * unscoped admin rather than defaulting to whatever org they are attached to.
 */
import { useCallback, useEffect, useState } from 'react';
import { LifeBuoy, Loader2 } from 'lucide-react';
import { apiFetch } from '../../stores/authStore';
// Shared with the owner's G9 card so the two surfaces cannot drift on what they
// say happened to an order.
import { recoveryOutcomeLabel } from '../../lib/triggerRunner/g9Scalp';
import type {
  ManagerTriggerRecoveryListResponse,
  ManagerTriggerRecoveryRun,
  TriggerRecoveryOrderOutcome,
  TriggerRecoveryState,
} from '../../lib/triggerRunner/types';

/** Fixed copy per state. `unverifiable` deliberately does not read as progress. */
const STATE_LABEL: Record<TriggerRecoveryState, string> = {
  'needs-manager-recovery': 'Manager recovery required',
  'winding-down': 'Recovery in progress',
  partial: 'Partly recovered',
  complete: 'Recovery complete',
  unverifiable: 'Cannot be verified',
};

const STATE_TONE: Record<TriggerRecoveryState, string> = {
  'needs-manager-recovery': 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  'winding-down': 'border-sky-500/40 bg-sky-500/10 text-sky-300',
  partial: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  complete: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400',
  unverifiable: 'border-red-500/40 bg-red-500/10 text-red-400',
};

const ACTION_LABEL: Record<'recover' | 'resume' | 'retry', string> = {
  recover: 'Recover',
  resume: 'Resume recovery',
  retry: 'Retry',
};

function outcomeTone(outcome: TriggerRecoveryOrderOutcome): string {
  if (outcome.result === 'canceled' || outcome.result === 'already-gone') {
    return 'text-emerald-400';
  }
  return 'text-red-400';
}

export function TriggerRecoveryPanel({
  adminMode,
  orgQueryParam,
  selectedOrgId,
  className = 'mb-6',
}: {
  adminMode?: boolean;
  /** `?orgId=…` for an admin, empty for a manager. Matches every other panel. */
  orgQueryParam: string;
  selectedOrgId: string;
  className?: string;
}) {
  const [runs, setRuns] = useState<ManagerTriggerRecoveryRun[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingRunId, setPendingRunId] = useState<string | null>(null);

  // An admin with nothing selected must not fetch: the endpoint refuses an
  // unscoped admin, and firing it anyway would render a refusal as an error.
  const scoped = !adminMode || !!selectedOrgId;

  const load = useCallback(async () => {
    if (!scoped) return;
    try {
      const res = await apiFetch(`/api/manager/trigger-runs/recovery${orgQueryParam}`);
      const data = (await res.json()) as ManagerTriggerRecoveryListResponse & { error?: string };
      if (!res.ok) {
        setLoadError(data.error ?? 'Could not load trigger recovery');
        setRuns(null);
        return;
      }
      setRuns(data.runs ?? []);
      setLoadError(null);
    } catch {
      setLoadError('Could not load trigger recovery');
      setRuns(null);
    }
  }, [orgQueryParam, scoped]);

  useEffect(() => {
    setRuns(null);
    setLoadError(null);
    setActionError(null);
    void load();
  }, [load]);

  const act = async (run: ManagerTriggerRecoveryRun) => {
    if (pendingRunId) return;
    setPendingRunId(run.id);
    setActionError(null);
    try {
      const res = await apiFetch(
        `/api/manager/trigger-runs/${run.id}/recover${orgQueryParam}`,
        { method: 'POST' },
      );
      const data = (await res.json()) as { error?: string };
      if (!res.ok) {
        setActionError(data.error ?? 'Recovery failed');
        return;
      }
      // The action response is authoritative, but the LIST is what this panel
      // renders — refetch so a second manager's concurrent attempt is visible too.
      await load();
    } catch {
      setActionError('Recovery failed');
    } finally {
      setPendingRunId(null);
    }
  };

  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`}>
      <div className="flex items-center gap-2">
        <LifeBuoy size={16} className="text-muted-foreground" />
        <h3 className="text-sm font-semibold">Trigger recovery</h3>
      </div>
      <p className="text-muted-foreground mt-1 text-[11px]">
        Triggers whose trader has left this organization are frozen before any
        further venue activity. Recovery cancels only the exact orders this
        organization's own audit ties to the run, using this organization's
        credentials. The moved trader cannot run, cancel, or clear them.
      </p>

      {!scoped ? (
        <div className="text-muted-foreground mt-3 text-xs">
          Select an organization to review its frozen triggers.
        </div>
      ) : loadError ? (
        <div className="mt-3 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
          {loadError}
        </div>
      ) : runs === null ? (
        <div className="text-muted-foreground mt-3 text-xs">Loading…</div>
      ) : runs.length === 0 ? (
        <div className="text-muted-foreground mt-3 text-xs">
          No trigger runs need recovery in this organization.
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {runs.map((run) => (
            <div key={run.id} className="border-border rounded-md border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{run.label}</div>
                  <div className="text-muted-foreground mt-0.5 text-[11px]">
                    Original trader{' '}
                    <span className="font-mono">
                      {run.originalTrader.username ?? run.originalTrader.id}
                    </span>
                    {' · '}
                    {run.recovery.eligibleOrderIds.length} order
                    {run.recovery.eligibleOrderIds.length === 1 ? '' : 's'} identified
                  </div>
                </div>
                <span
                  className={`shrink-0 rounded border px-2 py-0.5 text-[10px] font-bold ${STATE_TONE[run.recovery.state]}`}
                >
                  {STATE_LABEL[run.recovery.state]}
                </span>
              </div>

              {run.recovery.outcomes.length > 0 && (
                <div className="mt-2 space-y-1">
                  {run.recovery.outcomes.map((outcome) => (
                    <div
                      key={outcome.orderId}
                      className="flex items-center justify-between gap-3 text-[11px]"
                    >
                      <span className="truncate font-mono">{outcome.orderId}</span>
                      <span className={outcomeTone(outcome)}>{recoveryOutcomeLabel(outcome)}</span>
                    </div>
                  ))}
                </div>
              )}

              {run.recovery.outcomes.length === 0 && run.recovery.eligibleOrderIds.length > 0 && (
                <div className="mt-2 space-y-1">
                  {run.recovery.eligibleOrderIds.map((orderId) => (
                    <div key={orderId} className="truncate font-mono text-[11px]">
                      {orderId}
                    </div>
                  ))}
                </div>
              )}

              {run.recovery.reasons.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {run.recovery.reasons.map((reason, index) => (
                    <li key={`${reason.code}-${index}`} className="text-[11px] text-amber-300">
                      {reason.message}
                    </li>
                  ))}
                </ul>
              )}

              {run.action && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => void act(run)}
                    disabled={pendingRunId !== null}
                    className="bg-primary text-primary-foreground flex h-7 items-center gap-1.5 rounded px-3 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {pendingRunId === run.id && <Loader2 size={12} className="animate-spin" />}
                    {ACTION_LABEL[run.action]}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {actionError && (
        <div className="mt-2 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
          {actionError}
        </div>
      )}
    </section>
  );
}
